import Input from '../../../Input/Input';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object } from 'yup';
import { stringScheme } from 'src/utils/validation-schemes';
import { CHROME_EXTENSION_LINK, TRENGO_MAIN_PAGE } from 'src/shared/constants';
import type { SelectedIntegration } from 'src/store/slices/integrationSlice';
import type { IntegrationSubmitHandler } from 'src/components/Integrations/integrationFormTypes';

export const IntegrationFormTrengo = ({
  integration,
  onSubmit,
}: {
  integration?: SelectedIntegration;
  onSubmit?: IntegrationSubmitHandler;
}) => {
  const { handleSubmit, control, trigger } = useForm({
    defaultValues: {
      agency_id: integration.integration_data.agency_id || '',
      integration_type: integration.integration_type,
    },
    resolver: yupResolver(
      object({
        integration_type: stringScheme({ required: true }).label('Integration Type'),
        agency_id: stringScheme({ required: true }).label('Agency ID'),
      }),
    ),
  });

  const submit = (data: { agency_id: string; integration_type: string }) => {
    const request = {
      ...integration,
      integration_data: {
        ...integration.integration_data,
        agency_id: data.agency_id,
      },
    };

    onSubmit(request);
  };

  return (
    <form id="integration-form" onSubmit={handleSubmit(submit)}>
      <div className={'input-container'}>
        <Controller
          control={control}
          name="integration_type"
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Input
              autoFocus
              type={'text'}
              label={'Integration Type'}
              placeholder={'trengo'}
              invalidMessage={error?.message}
              value={value}
              isValid={!error?.message}
              onChange={onChange}
              readonly={true}
              onBlur={() => trigger('integration_type')}
            />
          )}
        />
      </div>
      <div className={'p-5 rounded-sm-5 bg-light flex flex-col gap-2.5 mt-2.5'}>
        <div>
          <p className={'text-ebony text-sm font-semibold'}>Set up Agency ID</p>
        </div>
        <ul
          className={'ml-4 list-decimal text-sm text-ebony_opacity_70 font-medium leading-[190%]'}
        >
          <li className={''}>
            Create an account on the{' '}
            <a
              className={'text-link italic underline font-semibold'}
              href={TRENGO_MAIN_PAGE}
              target={'_blank'}
              rel="noreferrer"
            >
              Trengo AI platform
            </a>
            {'.'}
          </li>
          <li>
            Install the{' '}
            <a
              className={'text-link italic underline font-semibold'}
              href={CHROME_EXTENSION_LINK}
              target={'_blank'}
              rel="noreferrer"
            >
              {' '}
              CoSupport AI Chrome Extension
            </a>{' '}
            and log in using CoSupport AI account credentials.
          </li>
          <li className={'align-middle'}>
            <span>Click the Agency ID section.</span>
            <span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                className={'inline ml-1 -mt-[2px]'}
              >
                <rect width="20.001" height="20" rx="10" fill="#ECF57B" />
                <path
                  d="M13.5619 11.9788L12.7706 11.1864L13.5619 10.3951C14.0828 9.86956 14.3744 9.15909 14.3728 8.41913C14.3711 7.67918 14.0765 6.96999 13.5532 6.44676C13.03 5.92353 12.3208 5.62886 11.5809 5.62724C10.8409 5.62562 10.1304 5.91718 9.60492 6.43811L8.81308 7.22995L8.0218 6.43867L8.81308 5.64739C9.54774 4.91273 10.5442 4.5 11.5831 4.5C12.6221 4.5 13.6185 4.91273 14.3532 5.64739C15.0878 6.38205 15.5006 7.37847 15.5006 8.41744C15.5006 9.4564 15.0878 10.4528 14.3532 11.1875L13.5619 11.9788ZM11.9788 13.5613L11.1875 14.3526C10.4528 15.0873 9.45641 15.5 8.41744 15.5C7.37847 15.5 6.38205 15.0873 5.64739 14.3526C4.91273 13.6179 4.5 12.6215 4.5 11.5826C4.5 10.5436 4.91273 9.54718 5.64739 8.81252L6.43923 8.02124L7.23051 8.81364L6.43923 9.60492C5.92544 10.1318 5.63993 10.8399 5.64454 11.5759C5.64915 12.3118 5.94352 13.0163 6.46387 13.5367C6.98422 14.0571 7.68866 14.3516 8.42458 14.3563C9.16051 14.361 9.86866 14.0756 10.3956 13.5619L11.1875 12.7706L11.9788 13.5613ZM11.5831 7.62559L12.375 8.41744L8.41744 12.3738L7.62615 11.5826L11.5831 7.62559Z"
                  fill="#05050D"
                />
              </svg>
            </span>
          </li>
          <li>
            <p>Click “Copy” and paste your Agency ID below:</p>
            <div className={'mt-2.5'}>
              <Controller
                control={control}
                name="agency_id"
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Input
                    autoFocus
                    type={'text'}
                    placeholder={'37382022'}
                    invalidMessage={error?.message}
                    value={value}
                    isValid={!error?.message}
                    onChange={onChange}
                    onBlur={() => trigger('agency_id')}
                  />
                )}
              />
            </div>
          </li>
        </ul>
      </div>
    </form>
  );
};
